export const PDF_FIELD_MAPPINGS: Record<string, string | null> = {
  // Time fields - handled specially with year/month split
  'text-employeeroleyear': 'timeInRoleYears',
  'text-employeerolemonth': 'timeInRoleMonths',
  'text-employeelevelyear': 'timeInLevelYears',
  'text-employeelevelmonth': 'timeInLevelMonths',
  'text-employeeamazonyear': 'timeInAmazonYears',
  'text-employeeamazonmonth': 'timeInAmazonMonths',

  // Job fields
  'text-employeejobcat': 'technicalClassification',
  'text-employeejobrole': 'jobFamily',
  'text-jobhistory': 'formattedJobHistory',

  // Summary fields
  'text-rolesummary': 'formattedRoleSummary',
  'text-primaryownership': 'formattedPrimaryOwnership',
  'text-justjfications': 'formattedJustification',

  // Calculated fields
  'text-talentcardperiod': 'talentCardPeriod',

  // Checkboxes - handled separately
  'checkbox-manager': 'isManager',
  'checkbox-barraiser': 'isBarRaiser'
};

/** A user-selectable input document-source category for org-card generation. */
export interface CardCategoryOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Input document-source categories the org-card worker reads from WorkDocs, surfaced as
 * checkboxes in Step 5. `value` MUST match the backend's accepted strings exactly — the
 * generate-org-cards API returns HTTP 400 on any unknown category.
 */
export const CARD_CATEGORY_OPTIONS: readonly CardCategoryOption[] = [
  { value: 'manager_hub_exports', label: 'Manager Hub Exports' },
  { value: 'supplemental_docs', label: 'Supplemental Context' }
] as const;

/** Canonical ordered list of valid category values — the guardrail set used for filtering. */
export const CARD_CATEGORY_VALUES: readonly string[] = CARD_CATEGORY_OPTIONS.map((option) => option.value);

export const MAX_EMPLOYEES = 250;

/** Verbatim limit message shown wherever the 250-employee cap is hit (warning in Step 1, error in Step 2). */
export const MAX_EMPLOYEES_LIMIT_MESSAGE = `Baseball cards support up to ${MAX_EMPLOYEES} employees.`;

export const BATCH_SIZE = 5;
export const BATCH_DELAY_MS = 500;
export const API_TIMEOUT_MS = 5000;

/** Maximum size (MB) for a single file uploaded to WorkDocs from the folder browser. */
export const UPLOAD_MAX_FILE_SIZE_MB = 4;

/**
 * Maximum documents allowed in a single source folder. The generation Lambda reads every file
 * in a folder and passes them to the Bedrock Converse API, which caps the number of documents
 * per request — so we block uploads that would push a folder's total (existing + newly selected)
 * over this limit.
 */
export const UPLOAD_MAX_DOCS_PER_FOLDER = 5;

/**
 * Maximum documents allowed in the "Manager Hub Exports" folder. Unlike other source folders,
 * this holds exactly one team-wide ratings export (a single `manager-hub*.xlsx`) — the Step 4
 * folder check and the generation processor both expect a single file — so uploads there are
 * capped at one.
 */
export const UPLOAD_MAX_MANAGER_HUB_DOCS = 1;

/**
 * `accept` attribute for the folder upload control. Covers the document types teams
 * put in the baseball-card source folders: CSV ratings, prior-cycle/annual-review PDFs,
 * and supplemental Office/image docs.
 */
export const UPLOAD_ACCEPTED_FILE_TYPES = '.csv,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.txt';
